import { supabase } from "@/integrations/supabase/client";
import { shouldSkipAnonymousSession } from "@/utils/authUser";
import { logError, logInfo } from "@/utils/logger";

let inflight: Promise<string | null> | null = null;

/**
 * Garante que existe uma sessão Supabase ativa para o visitante.
 * Se não houver, cria uma sessão anônima (Supabase Anonymous Sign-Ins).
 *
 * Retorna o user.id da sessão (anônima ou autenticada), ou null se falhar.
 *
 * Pré-requisito: o provider "Anonymous" precisa estar habilitado em
 * Supabase → Auth → Providers → Anonymous.
 */
export async function ensureAnonymousSession(): Promise<string | null> {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      if (shouldSkipAnonymousSession()) {
        return null;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        return session.user.id;
      }

      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) {
        // Caso comum: provider Anonymous desabilitado no painel.
        logError("Falha ao criar sessão anônima", error as Error, {
          component: "anonymousSession",
          action: "signInAnonymously",
        });
        return null;
      }

      logInfo("Sessão anônima criada", {
        component: "anonymousSession",
        action: "signInAnonymously",
      });
      return data.user?.id ?? null;
    } catch (e) {
      logError("Erro inesperado em ensureAnonymousSession", e as Error, {
        component: "anonymousSession",
      });
      return null;
    } finally {
      // libera após resolver para permitir nova tentativa em sessões posteriores
      setTimeout(() => { inflight = null; }, 0);
    }
  })();

  return inflight;
}

/**
 * Indica se a sessão atual é anônima (criada via signInAnonymously).
 */
export async function isAnonymousSession(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  // @ts-ignore – campo presente no JWT do Supabase
  return Boolean(session?.user?.is_anonymous);
}
